import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import menuClosed from '../assets/images/menu-closed.svg'
import useOuterClick from '../hooks/useOuterClick'
import { SessionUser } from './types'

interface MenuHeaderProps {
  user?: SessionUser | null
}

const formatRole = (role: string) =>
  role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// `roles` mixes job roles with permission badges (edit_user, tasks, …), so
// only these job roles are shown; anyone else gets no badge
const ROLE_PRIORITY = ['SUPERUSER', 'ADMIN', 'PRODUCT_REP', 'CALL_CENTER_AGENT']

const primaryRole = (roles: string[] = []) => {
  const normalized = roles.map((role) => role.toUpperCase())
  const role = ROLE_PRIORITY.find((r) => normalized.includes(r))
  return role ? formatRole(role) : ''
}

const MenuHeader = ({ user }: MenuHeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const innerRef = useOuterClick(() => {
    if (isMenuOpen) setIsMenuOpen(false)
  })

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMenuOpen])

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
  const displayName = fullName || user?.email || 'Account'
  const initials =
    (fullName || user?.email?.split('@')[0] || '')
      .split(/[\s._+-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?'
  const role = primaryRole(user?.roles)

  return (
    <div className="menu-header" ref={innerRef}>
      <button
        type="button"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="menu-header__trigger"
        data-testid="menu-header__open-icon"
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-controls="menu-header-options"
        aria-label={`${displayName} account menu`}
      >
        <span className="menu-header__avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="menu-header__name" title={displayName}>
          {displayName}
        </span>
        <img src={menuClosed} alt="" className="menu-header__open-icon" />
      </button>

      {isMenuOpen ? (
        <div className="menu-header__options-box" id="menu-header-options">
          <div className="menu-header__profile">
            <div className="menu-header__profile-name" title={displayName}>
              {displayName}
            </div>
            {user?.email && fullName ? (
              <div className="menu-header__profile-email" title={user.email}>
                {user.email}
              </div>
            ) : null}
            {role ? (
              <span className="menu-header__profile-role">{role}</span>
            ) : null}
          </div>
          <Link to="/logout" className="menu-header__option">
            Logout
          </Link>
        </div>
      ) : null}
    </div>
  )
}

export default MenuHeader
